import React, { useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { DialogContent, DialogTitle } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { useCurrentEditor } from '@tiptap/react';

import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import { flattenOptions } from '~/components/core/Editor/MenuBar/suggestion/suggestion';
import type { SelectedToken, SuggestionOption } from '~/components/core/Editor/types';
import TextField from '~/components/core/Molecules/Fields/TextField';
import HierarchicalSearchablePopover from '~/components/core/Molecules/HierarchicalSearchablePopover/HierarchicalSearchablePopover';
import SuggestionIcon from '~/components/icons/editor/SuggestionIcon';
import EMAIL_BODY_ALLOWED_URL_PROTOCOLS from '~/server_shared/generated-types/EMAIL_BODY_ALLOWED_URL_PROTOCOLS';

import { Heading } from '../../../TextComponents';

import { useLinkModal } from './LinkModalContext';

// Deliberately looser than the backend's `[A-Za-z0-9_]+` token-key pattern: a `{{claim-number}}` the
// backend would not even recognise as a token ships to the recipient verbatim, so the author has to
// see it flagged here rather than in the delivered email.
const TOKEN_PLACEHOLDER_PATTERN = /\{\{([^{}]*)\}\}/g;

// The one declaration of the protocols the outgoing email sanitizer keeps on an `<a href>`
// (`server/email_html_allowlist.py`, generated from `enums.json`). A URL with any other scheme has
// its whole anchor dropped at send time, so flag it here rather than let the author discover it in
// the delivered email.
const ALLOWED_URL_SCHEMES = new Set<string>(EMAIL_BODY_ALLOWED_URL_PROTOCOLS);

// A URL that does not match is a relative one, which the backend allows.
const URL_SCHEME_PATTERN = /^([a-zA-Z][a-zA-Z0-9+.-]*):/;

/** The URL's scheme when it has one the backend would refuse, else null. Tabs and newlines are
 *  stripped first because a browser — and `urlparse` on the backend — ignore them, so a scheme split
 *  across one is still that scheme. */
const findDisallowedScheme = (url: string): string | null => {
  const scheme = URL_SCHEME_PATTERN.exec(url.replace(/[\t\r\n]/g, '').trim())?.[1];
  return scheme && !ALLOWED_URL_SCHEMES.has(scheme.toLowerCase()) ? scheme : null;
};

/** The tree the picker offers: tokens the API marked unusable in a URL dropped, along with any group
 *  they leave empty. An option carrying no verdict stays offered — the render path reports an
 *  unusable token through the failed-tokens banner, so offering one degrades visibly, while hiding a
 *  usable one would not. */
const keepUrlSafeTokens = (options: SuggestionOption[]): SuggestionOption[] =>
  options.reduce<SuggestionOption[]>((kept, option) => {
    if (option.sub_items && option.sub_items.length > 0) {
      const subItems = keepUrlSafeTokens(option.sub_items);
      if (subItems.length > 0) {
        kept.push({ ...option, sub_items: subItems });
      }
    } else if (option.url_safe !== false) {
      kept.push(option);
    }
    return kept;
  }, []);

/** The `{{...}}` placeholders in `url` that the picker would not have offered, as the author wrote
 *  them. The key is upper-cased but not trimmed, matching how the render path resolves it. */
const findUnsupportedPlaceholders = (url: string, supportedKeys: Set<string>): string[] =>
  [...url.matchAll(TOKEN_PLACEHOLDER_PATTERN)]
    .filter(([, key]) => !supportedKeys.has(key.toUpperCase()))
    .map(([placeholder]) => placeholder);

export const LinkModal: React.FC = () => {
  const { editor } = useCurrentEditor();
  const { isOpen, closeModal, tokenOptions } = useLinkModal();
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [tokenPickerAnchorEl, setTokenPickerAnchorEl] = useState<HTMLButtonElement | null>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  // Where the caret goes once React has committed an insertion; null when none is pending.
  const pendingCaretRef = useRef<number | null>(null);

  const urlSafeTokenOptions = useMemo(() => keepUrlSafeTokens(tokenOptions), [tokenOptions]);
  const urlSafeTokenKeys = useMemo(
    () => new Set<string>(flattenOptions(urlSafeTokenOptions).map(({ id }: SuggestionOption) => id.toUpperCase())),
    [urlSafeTokenOptions]
  );
  const canPickTokens = urlSafeTokenOptions.length > 0;
  // An editor with no tokens at all keeps the plain dialog: no picker, and a `{{...}}` in its URL is
  // just text, exactly as before.
  const hasTokens = tokenOptions.length > 0;

  // Update the displayed URL onOpen - so it will display the selected URL.
  useEffect(() => {
    if (isOpen) {
      setUrl(editor?.getAttributes('link')?.href ?? '');
      setUrlError('');
    }
  }, [editor, isOpen]);

  // React rewrites the input's value on every insertion, which parks the caret at the end; put it
  // back where the token landed so the next one goes where the author is looking. The picker is a
  // Popover, which returns focus to the button that opened it as it closes, before this effect
  // runs, so the field takes it after.
  useEffect(() => {
    const caret = pendingCaretRef.current;
    if (caret === null) {
      return;
    }
    pendingCaretRef.current = null;
    urlInputRef.current?.focus();
    urlInputRef.current?.setSelectionRange(caret, caret);
  }, [url]);

  const onClose = () => {
    closeModal();
  };

  const removeLink = () => editor?.chain().focus().extendMarkRange('link').unsetLink().run();
  const saveLink = () => {
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
  };

  const onUrlChange = (value: string) => {
    setUrl(value);
    setUrlError('');
  };

  const onTokenSelect = ({ id }: SelectedToken) => {
    const placeholder = `{{${id}}}`;
    const input = urlInputRef.current;
    const selectionStart = input?.selectionStart ?? url.length;
    const selectionEnd = input?.selectionEnd ?? url.length;

    pendingCaretRef.current = selectionStart + placeholder.length;
    onUrlChange(`${url.slice(0, selectionStart)}${placeholder}${url.slice(selectionEnd)}`);
    setTokenPickerAnchorEl(null);
  };

  const onSave = () => {
    // The placeholder check needs the catalog, so it is skipped where there is none — the plain
    // dialog above. The scheme check reads only the URL, and three editors offer the link button
    // with no catalog at all (`EmailCommunicationSpecificBody` — the adjuster's own email composer —
    // `EmailAcknowledgmentBuilderContainer` and `InstructionsMarkdownEditorFormik`), so gating it on
    // the catalog too would drop it on two emailed surfaces.
    const unsupportedPlaceholders = hasTokens ? findUnsupportedPlaceholders(url, urlSafeTokenKeys) : [];
    const disallowedScheme = findDisallowedScheme(url);
    const errors = [
      unsupportedPlaceholders.length > 0 ? `Unsupported token(s) in URL: ${unsupportedPlaceholders.join(', ')}` : '',
      disallowedScheme ? `Unsupported URL scheme: ${disallowedScheme}` : '',
    ].filter(Boolean);
    if (errors.length > 0) {
      setUrlError(errors.join('. '));
      return;
    }
    url ? saveLink() : removeLink();
    closeModal();
  };

  const onClear = () => {
    removeLink();
    closeModal();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <div className="inline-flex w-full items-center justify-between">
          <Heading>Edit Link</Heading>
          <IconButton aria-label="Close" className="p-0">
            <CloseIcon onClick={closeModal} />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className="mb-12 [&&]:pt-8">
        <TextField
          id="link-url"
          variant="outlined"
          label="URL"
          value={url}
          fullWidth
          inputRef={urlInputRef}
          error={!!urlError}
          helperText={urlError || undefined}
          onChange={onUrlChange}
          InputProps={
            canPickTokens
              ? {
                  endAdornment: (
                    <IconButton
                      aria-label="Insert token"
                      className="p-0"
                      onClick={(event) => setTokenPickerAnchorEl(event.currentTarget)}
                    >
                      <SuggestionIcon />
                    </IconButton>
                  ),
                }
              : undefined
          }
        />
        {canPickTokens ? (
          <HierarchicalSearchablePopover
            open={Boolean(tokenPickerAnchorEl)}
            anchorEl={tokenPickerAnchorEl}
            options={urlSafeTokenOptions}
            onClose={() => setTokenPickerAnchorEl(null)}
            onSelect={onTokenSelect}
            label="Insert token"
            searchTestId="link-url-token-search"
          />
        ) : null}
        <div className="mt-20 inline-flex w-full justify-end">
          <Button onClick={onClear}>clear</Button>
          <Button onClick={onSave} variant="contained" color="primary">
            save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
